// Official eligibility data for the 88-day specified work requirement
// Source: immi.homeaffairs.gov.au — Working Holiday (subclass 417 & 462)
// Last updated: Feb 2026

// 417 visa — eligible postcode ranges
// Excludes major metropolitan areas (Sydney, Newcastle, Wollongong, Brisbane,
// Gold Coast, Melbourne, Perth, ACT)
export const POSTCODES_417 = {
  NSW: [
    [2311, 2312], [2328, 2411], [2420, 2490], [2536, 2551],
    [2575, 2594], [2618, 2739], [2787, 2898],
  ],
  QLD: [
    [4124, 4125], [4133, 4133], [4211, 4211], [4270, 4272],
    [4275, 4275], [4280, 4280], [4285, 4285], [4287, 4287],
    [4307, 4499], [4510, 4510], [4512, 4512], [4515, 4519],
    [4522, 4899],
  ],
  VIC: [
    [3139, 3139], [3211, 3334], [3340, 3424], [3430, 3649],
    [3658, 3749], [3753, 3753], [3756, 3756], [3758, 3758],
    [3762, 3762], [3764, 3764], [3778, 3781], [3783, 3783],
    [3797, 3797], [3799, 3799], [3810, 3909], [3921, 3925],
    [3945, 3974], [3979, 3979], [3981, 3996],
  ],
  WA: [
    [6041, 6044], [6083, 6084], [6121, 6126], [6200, 6799],
  ],
  // SA, TAS, NT: all postcodes eligible
  SA: "ALL",
  TAS: "ALL",
  NT: "ALL",
  // ACT: not classified as regional → not eligible
  ACT: "NONE",
};

// 462 visa — Northern Australia ONLY for tourism/hospitality
// (agriculture has wider eligibility, similar to 417 but with restrictions)
// Reference postcodes for Northern Australia:
//   - QLD north of Tropic of Capricorn: 4737 to 4899
//   - WA north of Tropic of Capricorn: 6537 to 6799
//   - NT: all
export const POSTCODES_462_NORTHERN = {
  QLD: [[4737, 4899]],
  WA: [[6537, 6799]],
  NT: "ALL",
};

// Eligible industries (official Home Affairs categories)
export const INDUSTRIES = [
  {
    key: "plant_animal",
    label: { en: "Plant & animal cultivation", fr: "Culture végétale & élevage", de: "Pflanzen- & Tierzucht", it: "Coltura piante & animali", es: "Cultivo plantas & animales", pt: "Cultivo plantas & animais" },
    examples: { en: "Fruit picking, farm work, dairy, livestock", fr: "Cueillette, fermes, élevage", de: "Obst pflücken, Bauernhof, Tierhaltung", it: "Raccolta frutta, agricoltura", es: "Recolección, granja, ganado", pt: "Colheita, fazenda, gado" },
    icon: "🌾",
    color: "#3B8268",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "fishing",
    label: { en: "Fishing & pearling", fr: "Pêche & perliculture", de: "Fischen & Perlenzucht", it: "Pesca & perlicoltura", es: "Pesca & perlicultura", pt: "Pesca & perlicultura" },
    examples: { en: "Commercial fishing, aquaculture", fr: "Pêche commerciale, aquaculture", de: "Kommerzielle Fischerei", it: "Pesca commerciale", es: "Pesca comercial", pt: "Pesca comercial" },
    icon: "🐟",
    color: "#3D7BB5",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "tree_farming",
    label: { en: "Tree farming & felling", fr: "Sylviculture & abattage", de: "Forstwirtschaft & Holzfällung", it: "Silvicoltura", es: "Silvicultura", pt: "Silvicultura" },
    examples: { en: "Forestry, logging", fr: "Foresterie, bûcheronnage", de: "Forstwirtschaft", it: "Selvicoltura", es: "Forestal", pt: "Florestal" },
    icon: "🌲",
    color: "#2A5C5A",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "mining",
    label: { en: "Mining", fr: "Mines", de: "Bergbau", it: "Industria mineraria", es: "Minería", pt: "Mineração" },
    examples: { en: "FIFO, drilling, mine site work", fr: "FIFO, forage, site minier", de: "FIFO, Bohrungen, Minenarbeit", it: "FIFO, lavoro in miniera", es: "FIFO, minería", pt: "FIFO, mineração" },
    icon: "⛏️",
    color: "#D95D39",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "construction",
    label: { en: "Construction", fr: "Construction & BTP", de: "Bauwesen", it: "Edilizia", es: "Construcción", pt: "Construção" },
    examples: { en: "Building, civil, residential", fr: "Bâtiment, génie civil", de: "Bauwesen", it: "Edilizia, opere civili", es: "Edificación, civil", pt: "Edificação, civil" },
    icon: "🏗️",
    color: "#E5A93D",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "disaster",
    label: { en: "Bushfire / flood recovery", fr: "Reconstruction post-catastrophe", de: "Katastrophenwiederaufbau", it: "Ricostruzione post-disastri", es: "Recuperación de desastres", pt: "Recuperação de desastres" },
    examples: { en: "Volunteer or paid recovery work", fr: "Bénévole ou payé", de: "Freiwillig oder bezahlt", it: "Volontariato o retribuito", es: "Voluntario o pagado", pt: "Voluntário ou pago" },
    icon: "🆘",
    color: "#9B3B1E",
    valid_417: true,
    valid_462: true,
  },
  {
    key: "tourism_hospitality",
    label: { en: "Tourism & hospitality (Northern AU)", fr: "Tourisme & hôtellerie (Nord AU)", de: "Tourismus & Gastgewerbe (Nord-AU)", it: "Turismo & ospitalità (Nord AU)", es: "Turismo & hostelería (Norte AU)", pt: "Turismo & hotelaria (Norte AU)" },
    examples: { en: "Bar, café, hotel — North only", fr: "Bar, café, hôtel — Nord uniquement", de: "Bar, Café, Hotel — nur Norden", it: "Bar, caffè, hotel — Solo Nord", es: "Bar, café, hotel — Solo Norte", pt: "Bar, café, hotel — Apenas Norte" },
    icon: "🍹",
    color: "#9B5DE5",
    valid_417: false,
    valid_462: true,
    northern_only: true,
  },
];

// Helper: check if a postcode is in a range list
function inRanges(pc, ranges) {
  if (ranges === "ALL") return true;
  if (ranges === "NONE") return false;
  if (!Array.isArray(ranges)) return false;
  return ranges.some(([lo, hi]) => pc >= lo && pc <= hi);
}

// Detect AU state from postcode (rough but accurate)
export function stateFromPostcode(pc) {
  if (pc >= 1000 && pc <= 2599) return "NSW";
  if (pc >= 2600 && pc <= 2618) return "ACT";
  if (pc >= 2619 && pc <= 2899) return "NSW";
  if (pc >= 2900 && pc <= 2999) return "ACT";
  if (pc >= 3000 && pc <= 3999) return "VIC";
  if (pc >= 4000 && pc <= 4999) return "QLD";
  if (pc >= 5000 && pc <= 5799) return "SA";
  if (pc >= 5800 && pc <= 5999) return "NT"; // overlap zone
  if (pc >= 6000 && pc <= 6797) return "WA";
  if (pc >= 7000 && pc <= 7799) return "TAS";
  if (pc >= 800 && pc <= 999) return "NT";
  return null;
}

// Main eligibility checker
// Returns: { eligible, state, visa, industry, reason }
export function checkEligibility({ postcode, visa, industryKey }) {
  const rawPc = String(postcode || "").trim();
  if (!rawPc) {
    // Location NOT provided (e.g. not detected on payslip) — flag as "needs location"
    // rather than a hard "not eligible" so the user can pick it manually.
    return { eligible: false, reason: "location_required", needsLocation: true };
  }
  const pc = parseInt(rawPc, 10);
  if (!pc || isNaN(pc)) {
    return { eligible: false, reason: "invalid_postcode" };
  }
  const state = stateFromPostcode(pc);
  if (!state) {
    return { eligible: false, reason: "invalid_postcode" };
  }
  const industry = INDUSTRIES.find((i) => i.key === industryKey);
  if (!industry) {
    return { eligible: false, state, reason: "invalid_industry" };
  }

  // Check industry × visa compatibility
  if (visa === "417" && !industry.valid_417) {
    return { eligible: false, state, industry, reason: "industry_not_417" };
  }
  if (visa === "462" && !industry.valid_462) {
    return { eligible: false, state, industry, reason: "industry_not_462" };
  }

  // Check postcode × visa
  let pcEligible = false;
  if (visa === "417") {
    const ranges = POSTCODES_417[state];
    pcEligible = inRanges(pc, ranges);
  } else if (visa === "462") {
    // 462: agriculture & most industries use 417-style regional rules
    // tourism/hospitality use Northern Australia only
    if (industry.northern_only) {
      const ranges = POSTCODES_462_NORTHERN[state];
      pcEligible = ranges ? inRanges(pc, ranges) : false;
    } else {
      const ranges = POSTCODES_417[state];
      pcEligible = inRanges(pc, ranges);
    }
  }

  if (!pcEligible) {
    return {
      eligible: false,
      state,
      industry,
      reason: industry.northern_only ? "not_northern" : "not_regional",
    };
  }

  return { eligible: true, state, industry, visa };
}

// Compute number of days between two ISO dates (inclusive)
export function daysBetween(startISO, endISO) {
  const start = new Date(startISO);
  const end = new Date(endISO);
  if (isNaN(start) || isNaN(end) || end < start) return 0;
  const ms = end - start;
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
}

// ============================================================================
//  Official 88-day calculator — Immigration Australia rules
// ============================================================================
// There are 4 contract modes, each with a different counting method.
export const CONTRACT_TYPES = [
  { key: "casual", icon: "📋", color: "#D95D39" },
  { key: "part_time", icon: "⏱️", color: "#3D7BB5" },
  { key: "piece_rate", icon: "🌾", color: "#3B8268" },
  { key: "fulltime", icon: "📅", color: "#2A5C5A" },
];

// Minimum legal wages (AUD/hour, from 01/07/2025).
// casual gets ~25% loading on top.
export const MIN_WAGE = {
  fulltime: 24.95,
  part_time: 24.95,
  casual: 29.31,
  piece_rate: 24.95, // 2024 Horticulture Award guarantees the same floor
};

// Hours/week → days/week (Casual & Part-time official table)
function hoursPerWeekToDays(hoursPerWeek) {
  if (hoursPerWeek >= 35.25) return 7;
  if (hoursPerWeek >= 28.25) return 6;
  if (hoursPerWeek >= 21.25) return 5;
  if (hoursPerWeek >= 14.25) return 4;
  if (hoursPerWeek >= 7.25) return 3;
  if (hoursPerWeek >= 4) return 1;
  return 0;
}

// Full-time contract: rest days included if 5+ days worked
function fulltimeDaysCounted(daysWorkedPerWeek) {
  const d = parseInt(daysWorkedPerWeek, 10);
  if (!d || d <= 0) return 0;
  if (d >= 5) return 7;
  return d;
}

/**
 * Compute eligible days for a work period based on contract type + inputs.
 * @returns {number} eligible days that count toward 88 / 179
 */
export function computeEligibleDays(period) {
  if (!period) return 0;
  const calendarDays = daysBetween(period.start, period.end);
  if (calendarDays <= 0) return 0;
  const weeks = calendarDays / 7;
  const type = period.contractType || "casual";

  if (type === "casual" || type === "piece_rate") {
    const physical = parseInt(period.daysWorked, 10);
    if (isNaN(physical) || physical < 0) return 0;
    // Cannot exceed calendar days
    return Math.min(physical, calendarDays);
  }

  if (type === "part_time") {
    const totalHours = parseFloat(period.hoursWorked);
    if (isNaN(totalHours) || totalHours <= 0 || weeks <= 0) return 0;
    const hoursPerWeek = totalHours / weeks;
    const daysPerWeek = hoursPerWeekToDays(hoursPerWeek);
    return Math.min(Math.round(daysPerWeek * weeks), calendarDays);
  }

  if (type === "fulltime") {
    const dpw = parseInt(period.daysPerWeek, 10) || 5;
    const counted = fulltimeDaysCounted(dpw);
    return Math.min(Math.round(counted * weeks), calendarDays);
  }

  return 0;
}

/**
 * Check if the user is underpaid versus the legal minimum.
 * @returns {null | { rate, minimum, type }}
 */
export function checkWage(period) {
  const rate = parseFloat(period?.hourlyRate);
  if (!rate || isNaN(rate)) return null;
  const type = period.contractType || "casual";
  const minimum = MIN_WAGE[type] || MIN_WAGE.casual;
  if (rate < minimum) return { rate, minimum, type };
  return null;
}
